import { FC } from 'react';
import { Stack } from '@mui/material';
import { Modal, NumberInput } from 'melp-design/components';
import { Controller, SubmitHandler, useForm } from 'react-hook-form-latest';
import { useTranslation } from 'react-i18next';
import { useAdminUpdateTransmissionAct } from 'store/admin-transmission-acts';
import { ModalProps } from 'store/modal';
import { Currency } from 'types/general';
import { usePredefinedToasts } from 'utils/Toast';
import { fromCents, toCents } from 'utils/general';

interface Values {
  openingBalance: number;
  closingBalance: number;
  currency: Currency;
}

interface Props extends ModalProps {
  transmissionActId: string;
  initialValues: Values;
}

export const EditTransmissionActModal: FC<Props> = ({
  closeModal,
  transmissionActId,
  initialValues,
}) => {
  const { t } = useTranslation();
  const predefinedToasts = usePredefinedToasts();

  const { control, handleSubmit } = useForm({
    defaultValues: {
      ...initialValues,
      openingBalance: fromCents(initialValues.openingBalance),
      closingBalance: fromCents(initialValues.closingBalance),
    },
  });

  const { mutate: updateTransmissionAct, isLoading } =
    useAdminUpdateTransmissionAct();

  const onSubmit: SubmitHandler<Values> = (values) => {
    updateTransmissionAct(
      {
        transmissionActId,
        data: {
          openingBalance: toCents(values.openingBalance),
          closingBalance: toCents(values.closingBalance),
        },
      },
      {
        onSuccess: () => {
          predefinedToasts.success.updated();
          closeModal();
        },
      },
    );
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Modal.Content
        title={t('common.edit')}
        actions={[
          {
            variant: 'neutral-outline',
            onClick: () => closeModal(),
            label: t('common.cancel'),
          },
          {
            variant: 'primary',
            label: t('common.submit'),
            type: 'submit',
            disabled: isLoading,
          },
        ]}
      >
        <Stack gap={2}>
          <Controller
            name="openingBalance"
            control={control}
            render={({ field: { name, value, onChange, ref } }) => (
              <NumberInput
                label={t('transmissionActs.openingBalance.label')}
                name={name}
                value={value}
                onChange={onChange}
                required
                key={value}
                ref={ref}
                InputProps={{
                  endAdornment: initialValues.currency,
                }}
              />
            )}
          />

          <Controller
            name="closingBalance"
            control={control}
            render={({ field: { name, value, onChange, ref } }) => (
              <NumberInput
                label={t('transmissionActs.closingBalance.label')}
                name={name}
                value={value}
                onChange={onChange}
                required
                key={value}
                ref={ref}
                InputProps={{
                  endAdornment: initialValues.currency,
                }}
              />
            )}
          />
        </Stack>
      </Modal.Content>
    </form>
  );
};
